import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NoteImage } from "@/components/mt/NoteImage";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory3() {
  const { lesson, course, accent } = findLesson("music-theory", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT3-Minor-Pentatonic-Blues-Animated.mp4"
        caption="From major, derive minor → pentatonic → blues"
        hint="≈ 44 sec · watch first"
      />
      <Chapter number="03" eyebrow="Music Theory · Chapter Three" title="Beyond major: the moodier scales">
        <p>
          The major scale is the friendly, sunlit room of Western music — but it isn't the
          whole house. There are scales that feel sad, scales that feel mysterious, scales that
          feel ancient or exotic. Almost every one of them can be derived from the major scale
          you've already learned, simply by <em>flattening</em> a few of its notes or
          <em> removing</em> some altogether.
        </p>
        <MTQuote>
          All of the scales we are going to write down are dependent on the major scale,
          because we've sort of invested a lot of time learning that. And now that we've
          learned the major scale, let's try and look at some of the other scales which can
          be derived from that but sound radically different.
        </MTQuote>
        <p>
          In this chapter we meet five new scales: the <strong>natural minor</strong>, the
          <strong> harmonic minor</strong>, the <strong>major pentatonic</strong>, the
          <strong> minor pentatonic</strong>, and the <strong>blues</strong>. Each one is a
          modification of the same seven major-scale degrees you already know.
        </p>
      </Chapter>

      <Chapter eyebrow="The sad cousin" title="Natural minor — flatten 3, 6, and 7">
        <Prose>
          <p>
            The natural minor scale is the major scale's sad cousin. The recipe is mechanical:
            take any major scale, then drop the <strong>3rd</strong>, the <strong>6th</strong>,
            and the <strong>7th</strong> by one semitone each. That is the entire transformation.
            Same root, same letter sequence, three flatter notes — and the mood swings from
            cheerful to introspective.
          </p>
        </Prose>

        <TheoryBox label="Natural-minor formula">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp; 2 &nbsp; ♭3 &nbsp; 4 &nbsp; 5 &nbsp; ♭6 &nbsp; ♭7</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (start with the major scale; flatten the 3rd, 6th, and 7th)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Let's build <strong>C minor</strong>. Start from C major: <span className="font-mono">C
            D E F G A B</span>. Flatten the E to E♭, the A to A♭, and the B to B♭. Result:
            <span className="font-mono text-amber-300"> C D E♭ F G A♭ B♭</span>. Three flats. The
            same rule applied to any other root produces that root's natural minor.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["naturalMinor"]} />

        <Prose>
          <p>
            Listen to it next to C major. The third — that single half-step lower — is what
            swings the whole emotion from positive to pensive. Major feels victorious; minor
            feels introspective, like a struggle, or like you've just lost something. <em>That</em>
            is the power of the third.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Same with a twist" title="Harmonic minor — keep the 7 natural">
        <Prose>
          <p>
            The harmonic minor is almost identical to the natural minor — but it leaves the
            <strong> 7th unflattened</strong>. So you flatten only the 3rd and the 6th; the 7th
            stays where the major scale put it. That tiny change — a half-step difference on a
            single note — gives the scale a dramatic, almost cinematic flavour.
          </p>
        </Prose>

        <TheoryBox label="Harmonic-minor formula">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp; 2 &nbsp; ♭3 &nbsp; 4 &nbsp; 5 &nbsp; ♭6 &nbsp; 7</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (3rd and 6th flattened; 7th stays natural — that's the "drama")
          </div>
        </TheoryBox>

        <Prose>
          <p>
            On C: <span className="font-mono text-amber-300">C D E♭ F G A♭ B</span>. Notice the
            unusually wide gap between A♭ and B — three semitones in a single step, an interval
            called an <strong>augmented second</strong>. That awkward leap is
            what gives the harmonic minor its distinctly Middle-Eastern, Spanish, or
            classical-baroque character.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["harmonicMinor"]} />

        <NoteImage
          src="/notes/music-theory/finals/03b-minor-scales-natural-harmonic-dorian.png"
          caption="Jason's note: a side-by-side comparison of the four most-useful minor flavours — Natural minor (♭3 ♭6 ♭7), Harmonic minor (♭3 ♭6, raised 7th — ♮7), Dorian (♭3 ♭7), and Melodic Jazz (♭3 only). One picture, four moods."
        />
      </Chapter>

      <Chapter eyebrow="Five-note scales" title="Pentatonic — drop two, keep five">
        <Prose>
          <p>
            Not every scale uses seven notes. Some of the most singable, most ear-worm-friendly
            scales in world music use only <strong>five</strong>. We call them <em>pentatonic</em>
            ("penta" = five). They come in two flavours, derived from the major and minor scales
            respectively.
          </p>
        </Prose>

        <TheoryBox label="Major pentatonic · 1 2 3 5 6">
          From the major scale, drop the <strong>4th</strong> and the <strong>7th</strong>.
          Those are the two notes that create the strongest pull and tension; without them the
          scale becomes warm, open, and almost impossible to play wrong. C major pentatonic:
          <span className="font-mono text-amber-300"> C D E G A</span>.
        </TheoryBox>

        <ScaleExplorer tonic="C" scales={["majorPentatonic"]} />

        <TheoryBox label="Minor pentatonic · 1 ♭3 4 5 ♭7">
          From the natural minor, drop the <strong>2nd</strong> and the <strong>♭6</strong>.
          The result is a favourite for rock, blues and pop guitar solos. C minor
          pentatonic: <span className="font-mono text-amber-300"> C E♭ F G B♭</span>.
        </TheoryBox>

        <ScaleExplorer tonic="C" scales={["minorPentatonic"]} />

        <NoteImage
          src="/notes/music-theory/finals/03a-pentatonic-major-and-minor.png"
          caption="Pentatonic at a glance — Major pentatonic = 1 2 3 5 6 (drop 4 and 7). Minor pentatonic = 1 ♭3 4 5 ♭7 (drop 2 and ♭6). Both have five notes; both omit the strongest-pulling tones, which is why they sound forgiving."
        />

        <Prose>
          <p>
            The major pentatonic feels open and pastoral — think folk songs and country
            melodies. The minor pentatonic feels gritty and conversational — think rock riffs
            and blues licks. Both are five notes; both omit the most "leaning" intervals; both
            are forgiving to improvise over.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The exotic one" title="The blues scale — minor pentatonic plus the blue note">
        <Prose>
          <p>
            The blues scale is the minor pentatonic with one extra note slipped in:
            the <strong>♭5</strong>, also called the <em>blue note</em>. That tritone-flavoured
            half-step between the 4 and the 5 is the entire emotional fingerprint of the blues.
            Six notes; instantly recognisable.
          </p>
        </Prose>

        <TheoryBox label="Blues-scale formula">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp; ♭3 &nbsp; 4 &nbsp; ♭5 &nbsp; 5 &nbsp; ♭7</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (minor pentatonic + the "blue note" between 4 and 5)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            On C: <span className="font-mono text-amber-300">C E♭ F G♭ G B♭</span>. The G♭ → G
            crawl is the unmistakable wail you hear in every Chicago blues solo, every B.B.
            King bend, every slow-shuffle turnaround. It's the scale that turned American folk
            music into a global vocabulary.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["blues"]} />

        <NoteImage
          src="/notes/music-theory/finals/03c-blues-scales-major-and-minor.png"
          caption="The blues scales derived from the pentatonic. Major Blues = 1 2 ♭3 3 5 6 (a major pentatonic with the ♭3 'crush' note). Minor Blues = 1 ♭3 4 ♭5 5 ♭7 (a minor pentatonic with the ♭5 'blue' note). Both blues scales are six notes — pentatonic plus one blue."
        />
        <NoteImage
          src="/notes/music-theory/finals/03d-major-blues-on-staff.png"
          caption="The major blues scale written on the staff in B♭ — showing all six notes laid out, with the ♭3 highlighted as the crush note that gives the scale its blues colour."
        />
      </Chapter>

      <Chapter eyebrow="Side by side" title="Hear them all on the same root">
        <Prose>
          <p>
            The fastest way to internalise these scales is to switch between them on a single
            root and listen for the personality change. Drop your left hand on a steady C in the
            bass, then noodle up and down each scale in the right hand. The differences become
            obvious very quickly: natural minor pensive, harmonic minor dramatic, pentatonic
            open, blues smoky.
          </p>
        </Prose>

        <ScaleExplorer
          tonic="C"
          scales={["naturalMinor", "harmonicMinor", "majorPentatonic", "minorPentatonic", "blues"]}
        />
      </Chapter>

      <Chapter eyebrow="How to practise" title="One drone, free improvisation">
        <Steps>
          <Step>
            <strong>Pick a root.</strong> Start on C; eventually do this on every root, including
            the awkward sharps and flats.
          </Step>
          <Step>
            <strong>Hold the root in the left hand.</strong> A steady drone tells the ear "this
            is home" so the new scale's flavour stands out.
          </Step>
          <Step>
            <strong>Play each scale up and down twice.</strong> Hear the formula in your fingers
            before you try to improvise.
          </Step>
          <Step>
            <strong>Improvise freely.</strong> Wander up, down, skip notes, repeat motifs. There
            are no wrong notes inside the scale — only choices.
          </Step>
          <Step>
            <strong>Switch scales without moving the drone.</strong> That juxtaposition is where
            the emotional fingerprints become unmistakable.
          </Step>
        </Steps>

        <Prose>
          <p>
            Write each of these five scales out on at least four different roots. Then play
            them. We will revisit each one later in the course — minor scales for chord theory,
            blues for improvisation, pentatonics for melodic phrasing — so the more comfortable
            they are now, the easier those chapters become.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/music-theory-4"
        label="Chapter 4 · Convert Major to Minor & vice-versa"
        why="Now that you know the formulas, the next chapter turns it into a party trick: take any major-key song and turn it minor (or vice versa) by changing two notes."
      />
    </LessonShell>
  );
}
